import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import MarkdownMessage from '../../../assistant/components/MarkdownMessage.jsx';
import { formatAIChatTime } from '../Messaging/shared/dateUtils';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ShareIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { ChartBarIcon, ShieldCheckIcon, DocumentTextIcon, Squares2X2Icon } from '@heroicons/react/24/solid';

// Agent metadata
const AGENT_INFO = {
  'valuation_chat_agent': {
    name: 'Valuation Specialist',
    icon: ChartBarIcon,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  },
  'continuity_guardian_agent': {
    name: 'Continuity Specialist',
    icon: ShieldCheckIcon,
    color: 'text-green-600',
    bgColor: 'bg-green-50'
  },
  'content_creation_copilot_agent': {
    name: 'Content Co-Pilot',
    icon: DocumentTextIcon,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  },
  'succession_dashboard_agent': {
    name: 'Succession Dashboard',
    icon: Squares2X2Icon,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50'
  }
};

/**
 * ConversationViewer - View and export past conversations
 *
 * Features:
 * - Display full conversation history
 * - Export as JSON or Markdown
 * - Copy/share conversation
 * - Show conversation metadata
 */
const ConversationViewer = ({ conversation, onBack }) => {
  const intl = useIntl();
  const [exportFormat, setExportFormat] = useState('json');

  if (!conversation) {
    return (
      <div className="bg-white rounded-lg border border-succession-border p-12 text-center">
        <ChatBubbleLeftRightIcon className="h-12 w-12 text-succession-text-muted mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
          {intl.formatMessage({ id: 'succession.agent_system.conversation_viewer.no_conversation_selected' })}
        </h3>
        <p className="text-sm text-succession-text-secondary">
          {intl.formatMessage({ id: 'succession.agent_system.conversation_viewer.select_from_history' })}
        </p>
      </div>
    );
  }

  const agentInfo = AGENT_INFO[conversation.agentType] || {};
  const Icon = agentInfo.icon || ChatBubbleLeftRightIcon;

  // Mock messages for now (in real app, would load from backend)
  const messages = [
    {
      id: '1',
      role: 'agent',
      content: `Hello! I'm your ${agentInfo.name}. How can I help you today?`,
      timestamp: conversation.startedAt
    },
    {
      id: '2',
      role: 'user',
      content: 'I need help understanding my practice valuation.',
      timestamp: new Date(new Date(conversation.startedAt).getTime() + 60000).toISOString()
    },
    {
      id: '3',
      role: 'agent',
      content: 'I can help you with that! Practice valuation depends on several factors including:\n\n• Revenue trends\n• Client demographics\n• Growth potential\n• Market conditions\n\nWhat specific aspect would you like to explore?',
      timestamp: new Date(new Date(conversation.startedAt).getTime() + 120000).toISOString()
    }
  ];

  const formatDate = (dateString) => {
    const locale = (typeof window !== 'undefined' && window.locale) || 'en';
    return new Date(dateString).toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportConversation = () => {
    let content;
    let filename;
    let mimeType;

    if (exportFormat === 'json') {
      content = JSON.stringify({
        conversation: {
          id: conversation.id,
          agentType: conversation.agentType,
          agentName: agentInfo.name,
          startedAt: conversation.startedAt,
          lastActivity: conversation.lastActivity
        },
        messages: messages
      }, null, 2);
      filename = `conversation-${conversation.id}.json`;
      mimeType = 'application/json';
    } else {
      // Markdown format
      content = `# Conversation with ${agentInfo.name}\n\n`;
      content += `**Started:** ${formatDate(conversation.startedAt)}\n`;
      content += `**Last Activity:** ${formatDate(conversation.lastActivity)}\n\n`;
      content += `---\n\n`;

      messages.forEach((msg) => {
        const label = msg.role === 'user' ? 'You' : agentInfo.name;
        content += `### ${label} - ${formatDate(msg.timestamp)}\n\n`;
        content += `${msg.content}\n\n`;
      });

      filename = `conversation-${conversation.id}.md`;
      mimeType = 'text/markdown';
    }

    // Create download
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = () => {
    const content = messages.map(msg => {
      const label = msg.role === 'user' ? 'You' : agentInfo.name;
      return `${label}: ${msg.content}`;
    }).join('\n\n');

    navigator.clipboard.writeText(content);
    alert(intl.formatMessage({ id: 'succession.agent_system.conversation_viewer.copied' }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg border border-succession-border p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <button
              onClick={onBack}
              className="flex-shrink-0 p-2 hover:bg-succession-bg-secondary rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-succession-text-secondary" />
            </button>

            <div className={`flex-shrink-0 w-12 h-12 ${agentInfo.bgColor} rounded-lg flex items-center justify-center`}>
              <Icon className={`h-6 w-6 ${agentInfo.color}`} />
            </div>

            <div>
              <h2 className="text-xl font-semibold text-succession-text-primary">
                {agentInfo.name}
              </h2>
              <div className="mt-2 space-y-1 text-sm text-succession-text-secondary">
                <p>{intl.formatMessage({ id: 'succession.agent_system.conversation_viewer.started' })} {formatAIChatTime(conversation.startedAt)}</p>
                <p>{intl.formatMessage({ id: 'succession.agent_system.conversation_viewer.last_activity' })} {formatAIChatTime(conversation.lastActivity)}</p>
                <p>{intl.formatMessage({ id: 'succession.agent_system.conversation_viewer.messages' })} {messages.length}</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="px-3 py-2 border border-succession-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-succession-primary"
            >
              <option value="json">JSON</option>
              <option value="markdown">Markdown</option>
            </select>

            <button
              onClick={exportConversation}
              className="inline-flex items-center gap-2 px-4 py-2 bg-succession-primary text-white rounded-lg hover:bg-succession-primary-hover transition-colors"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              {intl.formatMessage({ id: 'succession.agent_system.conversation_viewer.export' })}
            </button>

            <button
              onClick={copyToClipboard}
              className="inline-flex items-center gap-2 px-4 py-2 border border-succession-border bg-white text-succession-text-primary rounded-lg hover:bg-succession-bg-secondary transition-colors"
            >
              <ShareIcon className="h-5 w-5" />
              {intl.formatMessage({ id: 'succession.agent_system.conversation_viewer.copy' })}
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="bg-white rounded-lg border border-succession-border shadow-sm">
        <div className="p-6 space-y-4">
          {messages.map((message, index) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-succession-primary text-white'
                    : 'bg-succession-bg-secondary text-succession-text-primary border border-succession-border'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-medium ${
                    message.role === 'user' ? 'text-white opacity-90' : 'text-succession-text-secondary'
                  }`}>
                    {message.role === 'user' ? intl.formatMessage({ id: 'succession.agent_system.common.you' }) : agentInfo.name}
                  </span>
                  <span className={`text-xs ${
                    message.role === 'user' ? 'text-white opacity-70' : 'text-succession-text-muted'
                  }`}>
                    {formatAIChatTime(message.timestamp)}
                  </span>
                </div>

                {message.role === 'user' ? (
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                ) : (
                  <div className="chat_result" dangerouslySetInnerHTML={{ __html: message.content }} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Export Preview */}
      {exportFormat === 'json' && (
        <div className="bg-gray-900 rounded-lg p-4 overflow-auto">
          <pre className="text-xs text-green-400 font-mono">
            {JSON.stringify({
              conversation: {
                id: conversation.id,
                agentType: conversation.agentType,
                agentName: agentInfo.name
              },
              messages: messages.slice(0, 2) // Preview first 2 messages
            }, null, 2)}
          </pre>
          <p className="text-xs text-gray-500 mt-2">
            {intl.formatMessage({ id: 'succession.agent_system.conversation_viewer.preview_label' })}
          </p>
        </div>
      )}
    </div>
  );
};

ConversationViewer.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.string.isRequired,
    agentType: PropTypes.string.isRequired,
    startedAt: PropTypes.string.isRequired,
    lastActivity: PropTypes.string.isRequired
  }),
  onBack: PropTypes.func
};

export default ConversationViewer;
